import {
  Alert,
  Button,
  Col,
  Form,
  List,
  Modal,
  Space,
  Tag,
  theme,
  Typography,
  message,
} from "antd";
import { ReactNode, useEffect, useState } from "react";
import { getPlatformUsers } from "../repository/data.repository";
import { retry } from "../utils/http";
import { PlatformUser } from "../repository/data.default";
import { nameOf } from "../utils/typescript";
import { QueryOperation } from "../code/search-query/search-query";
import { InputObject } from "../controls/default";
import { DynamicControl } from "../controls/DynamicForm.default";
import DynamicForm from "../controls/DynamicForm";
import GroupedTargetPicker from "../controls/GroupedTargetPicker";
import TargetingPreview from "../controls/TargetingPreview";
import { platformWideTargets } from "./calendar/calendarEventForm";
import _ from "lodash";

export type AssignTargetType = "company" | "department" | "user";

// Per-target assignment POST. Receives the chosen target uid + the (validated) entity-form values
// so each entity (reward needs dates, material needs isRequired) can shape its own payload.
export type AssignFn = (
  targetUid: string,
  values: any,
) => Promise<unknown> | undefined;

export interface AssignEntityFormDialogProps {
  readonly open?: boolean;
  readonly title: string;
  // The controls of the entity form (the entity picker itself + any extra controls, e.g. dates or an
  // Is Required toggle). The field named by `entityField` gates the Assign button.
  readonly entityControls: DynamicControl[];
  readonly entityField: string;
  // Optional contextual banner shown at the top of the dialog (e.g. how this entity's reach/scoping works).
  readonly helper?: ReactNode;
  readonly assignFns: Record<AssignTargetType, AssignFn>;
  // Optional client-side guard run before fan-out (e.g. reward's date validation). Return false to abort.
  readonly canSubmit?: (values: any) => boolean;
  // When true and EVERY target succeeds, close the dialog (via onFinish, refreshing the grid) with a toast
  // instead of showing the per-target results / "New Assignments" screen. On any failure it still stays open.
  readonly closeOnSuccess?: boolean;
  // Seed the entity form (e.g. a pre-selected reward).
  readonly initialEntityValues?: Record<string, any>;
  // Pre-load a single fixed user target (e.g. assigning from a user card).
  readonly target?: {
    readonly type: "user";
    readonly uid: string;
  };
  // Whether the reach preview counts sub-companies for company targets — true only for entities
  // whose company assignment cascades (materials); rewards/badges assign to the company directly.
  readonly previewIncludesSubCompanies?: boolean;
  readonly onFinish?: () => void;
  readonly onCancel?: () => void;
}

// Config-driven assignment dialog: pick an entity + one-or-more targets (companies/departments/users)
// → Promise.allSettled fan-out to the per-target assignment POST endpoints, with per-target Success/Failure
// tags. Shared by AssignRewardFormDialog and AssignMaterialFormDialog.
function AssignEntityFormDialog(props: AssignEntityFormDialogProps) {
  const { token } = theme.useToken();
  const [form] = Form.useForm();
  const values = Form.useWatch<Record<string, any>>([], form);
  const [loading, setLoading] = useState(false);
  const [finish, setFinish] = useState(false);
  const [targets, setTargets] = useState<InputObject[]>([]);
  const [result, setResult] = useState<
    { readonly uid: string; readonly success: boolean }[]
  >([]);

  // Lifecycle

  useEffect(() => {
    if (props.open) {
      if (props.initialEntityValues) {
        form.setFieldsValue(props.initialEntityValues);
      }
      if (props.target) {
        retry(() =>
          getPlatformUsers({
            searchQuery: {
              conditions: [
                {
                  field: nameOf<PlatformUser>("uid"),
                  operation: QueryOperation.Equal,
                  values: [props.target?.uid!],
                },
              ],
            },
            pageNumber: 0,
            pageSize: 1,
          }),
        ).then(({ data }) =>
          setTargets(
            data.map((dataItem) => ({
              display: [dataItem.firstName, dataItem.lastName].join(" "),
              subDisplay: [dataItem.companyName, dataItem.departmentName]
                .filter((f) => !!f)
                .join(" / "),
              value: dataItem.uid,
              dataItem: { ...dataItem, __name: "User", __type: "user" },
            })),
          ),
        );
      }
    } else {
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  // Events

  // Stage a picked target (the picker clears itself after each pick); de-dupe by uid.
  function onPickTarget(target: InputObject): void {
    setTargets((prev) => _.uniqBy([...prev, target], "value"));
  }

  function onSubmit(): void {
    if (props.canSubmit && !props.canSubmit(values)) {
      return;
    }
    setLoading(true);
    const result: { uid: string; success: boolean }[] = [];
    const promises = targets.map((target) => {
      const type = target.dataItem?.__type as AssignTargetType;
      const done = (success: boolean) =>
        result.push({ uid: target.value, success });
      const call = props.assignFns[type]?.(target.value, values);
      return Promise.resolve(call)
        .then(() => done(true))
        .catch(() => done(false));
    });

    Promise.allSettled(promises).then(() => {
      setLoading(false);

      // All targets succeeded → close + refresh (+ toast) instead of the results screen, when opted in.
      const allSucceeded =
        result.length > 0 && result.every((r) => r.success);
      if (props.closeOnSuccess && allSucceeded) {
        message.success(
          `Assigned to ${result.length} ${
            result.length === 1 ? "target" : "targets"
          }.`,
        );
        reset();
        props.onFinish?.();
        return;
      }

      setResult(result);
      setFinish(true);
    });
  }

  function onCancel(): void {
    reset();
    props.onCancel?.();
  }

  function onNewAssignments(): void {
    reset();
  }

  // Methods

  function reset(): void {
    setLoading(false);
    setTargets([]);
    setFinish(false);
    setResult([]);
    form.resetFields();
  }

  return (
    <Modal
      open={props.open}
      centered
      title={props.title}
      footer={null}
      maskClosable={false}
      // The shell must wrap the fixed-width content Col below (the InModal idiom) — with the
      // default 520px Modal width the 720px content spilled past the modal's right edge.
      width={"auto"}
      onCancel={onCancel}
    >
      <Col className="flex max-h-[512px] w-[720px] flex-col overflow-hidden overflow-y-auto">
        {props.helper && (
          <Alert
            className="mb-3"
            type="info"
            showIcon
            message={props.helper}
          />
        )}
        <Col className="mb-4 flex flex-col">
          <DynamicForm
            className="w-full"
            form={form}
            schema={{ groups: [{ controls: props.entityControls }] }}
          />
          <GroupedTargetPicker
            className="w-full"
            label="Target"
            placeholder="Search companies, departments or users"
            disabled={loading}
            loadGroups={(text) => platformWideTargets(text)}
            onPick={onPickTarget}
          />
          {!!targets.length && (
            <List
              className="m-2"
              style={{
                backgroundColor: token.colorBgLayout,
                borderRadius: token.borderRadius,
              }}
              dataSource={targets}
              renderItem={(item) => (
                <List.Item className="p-1 pl-2 pr-2">
                  <Col className="flex flex-auto flex-col">
                    <Typography.Text>{item.display}</Typography.Text>
                    <Typography.Text
                      style={{ color: token.colorTextDescription }}
                    >
                      {item.dataItem?.__name}
                    </Typography.Text>
                  </Col>
                  {result.some(
                    ({ uid, success }) => item.value === uid && success,
                  ) && <Tag color="green">Success</Tag>}
                  {result.some(
                    ({ uid, success }) => item.value === uid && !success,
                  ) && <Tag color="red">Failure</Tag>}
                </List.Item>
              )}
            />
          )}
          {/* Reach preview for the staged set — pre-submit only (the results screen shows reality). */}
          {!!targets.length && !finish && (
            <TargetingPreview
              className="ml-2 mr-2"
              targets={targets.map((item) => ({
                type: (item.dataItem?.__type ?? "user") as AssignTargetType,
                uid: item.value,
                label: item.display,
              }))}
              includeSubCompanies={props.previewIncludesSubCompanies}
            />
          )}
        </Col>
        {/* Actions */}
        <Space className="sticky bottom-0 flex flex-row items-center justify-end">
          <Button disabled={loading} onClick={onCancel}>
            Cancel
          </Button>
          {!finish && (
            <Button
              type="primary"
              loading={loading}
              disabled={!targets.length || !values?.[props.entityField]}
              onClick={onSubmit}
            >
              Assign
            </Button>
          )}
          {finish && (
            <Button type="primary" onClick={onNewAssignments}>
              New Assignments
            </Button>
          )}
        </Space>
      </Col>
    </Modal>
  );
}

export default AssignEntityFormDialog;
